import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import type { CurrentUser, LoginRequest, UserSpace } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { clearPreferenceCache, hydratePreferences } from '../../shared/prefs/store.ts';
import { clearLoggedOut, loginRequest, logoutRequest, markLoggedOut, meRequest } from './api.ts';

/**
 * React-контекст аутентификации (`docs/modules/auth.md §4`).
 *
 * Хранит трио состояний:
 *  - `status` — загрузка/анонимный/залогинен. Отдельный `loading`
 *    критичен для ProtectedRoute: без него при первом рендере
 *    можно промигать /login ещё до того, как отработал /api/auth/me.
 *  - `user` — DTO из /api/auth/me (тип CurrentUser из @lens/shared);
 *  - методы `login` / `logout` — единый путь к мутациям состояния.
 *
 * Используем чистый React Context, а не Zustand: состояний мало,
 * подписчиков мало, и один провайдер сверху достаточно. Если позже
 * понадобится selector-оптимизация — мигрируем точечно.
 */

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  user: CurrentUser | null;
  /**
   * Список пространств, в которых пользователь является участником,
   * с его ролью в каждом. Источник — `/api/auth/me`. Используется в
   * UI для скрытия/показа кнопок, доступных только не-наблюдателям
   * (например, кнопка Lensy в шапке).
   */
  spaces: UserSpace[];
  /**
   * Может ли пользователь пользоваться AI вне scope конкретного
   * пространства (Lensy в шапке, /api/ai/conversations,
   * /api/ai/models). Зеркалит серверный middleware `requireNonViewer`:
   * допуск, если `isPlatformAdmin` или есть хотя бы одна не-viewer
   * membership. Чисто-наблюдатель → false.
   */
  canUseAi: boolean;
  login: (input: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  patchUser: (patch: Partial<CurrentUser>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [spaces, setSpaces] = useState<UserSpace[]>([]);

  // На маунте дёргаем /me: если сессия жива (cookie в браузере),
  // мы сразу узнаем, кто залогинен, без явного логина.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await meRequest();
        if (cancelled) return;
        // Подтягиваем персональные настройки в localStorage-кэш ДО того,
        // как смонтируются страницы, читающие его синхронно.
        await hydratePreferences(me.user.id);
        if (cancelled) return;
        setUser(me.user);
        setSpaces(me.spaces);
        setStatus('authenticated');
      } catch (err) {
        if (cancelled) return;
        // 401 — штатно: просто нет сессии. Остальные ошибки тоже
        // трактуем как «не авторизован» на уровне UI — боковая
        // диагностика останется в консоли.
        if (!(err instanceof ApiError) || err.status !== 401) {
          console.error('auth: /me failed', err);
        }
        setUser(null);
        setSpaces([]);
        setStatus('anonymous');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (input: LoginRequest): Promise<void> => {
    const nextUser = await loginRequest(input);
    clearLoggedOut();
    await hydratePreferences(nextUser.id);
    setUser(nextUser);
    setStatus('authenticated');
    // loginRequest возвращает только user. Чтобы получить spaces (и
    // обновить canUseAi), сразу дёргаем /me. Не блокируем общий
    // success: при сбое /me просто оставим пустой список — следующий
    // mount AuthProvider'а перезапросит.
    try {
      const me = await meRequest();
      setSpaces(me.spaces);
    } catch (err) {
      console.error('auth: /me after login failed', err);
      setSpaces([]);
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await logoutRequest();
    } finally {
      // Даже если сервер ответил ошибкой, чистим локальный state:
      // пользователь нажал «Выйти», UI должен сразу отреагировать.
      // Кэш настроек тоже чистим — на общей машине следующий пользователь
      // не должен унаследовать (и замигрировать себе) чужие настройки.
      clearPreferenceCache();
      markLoggedOut();
      setUser(null);
      setSpaces([]);
      setStatus('anonymous');
    }
  }, []);

  const patchUser = useCallback((patch: Partial<CurrentUser>) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  // Зеркалит серверный middleware `requireNonViewer` (auth.md §4.2):
  // platform_admin или хотя бы одна membership с role !== 'viewer'.
  const canUseAi = useMemo(
    () =>
      Boolean(
        user &&
          (user.isPlatformAdmin || spaces.some((s) => s.role !== 'viewer')),
      ),
    [user, spaces],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, spaces, canUseAi, login, logout, patchUser }),
    [status, user, spaces, canUseAi, login, logout, patchUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
